import { useEffect } from "react";
import { atom, useAtomValue } from "jotai";
import { readStoredTimeSettings, resolveUserZone, setTimeSettings, storeTimeSettings, TimeRole, TimeSettings, zoneFor } from "./zone";

/**
 * Settings > Date & time, stored as `time` in the user settings blob (POST /API/v2/user). TED
 * replaces the whole blob on every save, so this atom is read and written by the `userState`
 * selector in lib/store.ts like every other blob key; leaving it out there would let the next theme
 * or color save wipe it.
 */
// The default is the localStorage mirror (the default on the server), so the first paint after a
// reload, and visitors, already follow the setting before the user settings load.
export const timeSettingsState = atom<TimeSettings>(readStoredTimeSettings());

/** S1 resolved to an IANA zone. Components that call this re-render when the setting changes. */
export const useUserZone = (): string => resolveUserZone(useAtomValue(timeSettingsState));

/** The zone for a role, as a hook (see zone.ts for the roles). */
export const useZoneFor = (role: TimeRole, exchangeTz?: string | null): string => zoneFor(role, useAtomValue(timeSettingsState), exchangeTz);

/** Keeps the non-React copy (dateParser, grid cells) and the localStorage/cookie mirrors in step. */
export const useTimeSettingsSync = () => {
    const settings = useAtomValue(timeSettingsState);
    // Set during render, so children rendering in this pass already format with the new zone; the
    // storage mirrors are side effects, so they wait for the effect.
    setTimeSettings(settings);
    useEffect(() => storeTimeSettings(settings), [settings]);
};
